import { getWindowHeight, getDocumentHeight } from '../helpers/viewport'
import { useEffect, useRef } from 'react'

const viewport = {
  windowHeight: getWindowHeight(),
  documentHeight: getDocumentHeight(),
  scrollTop: window.pageYOffset || document.documentElement.scrollTop,
  scrollBottom:
    window.pageYOffset || document.documentElement.scrollTop + getWindowHeight()
}

// keeping the method outside ensure that event listeners added
// in different components always refers to the same function
const update = () => {
  viewport.windowHeight = getWindowHeight()
  viewport.documentHeight = getDocumentHeight()
  viewport.scrollTop = window.pageYOffset || document.documentElement.scrollTop
  viewport.scrollBottom =
    (window.pageYOffset || document.documentElement.scrollTop) +
    getWindowHeight()
}

/**
 * Custom hook to get access to viewport metrics and bind event
 * listeners from components
 *
 * @param {Object} eventListeners custom callbacks to add to window events listeners
 * @param {function} eventListeners.scroll custom callback for scroll listener
 * @param {function} eventListeners.resize custom callback for resize listener
 * @returns {Array} an object containing viewport metrics, a function to update them
 * and a function to replace previous listeners
 * @requires viewport.getWindowHeight
 * @requires viewport.getDocumentHeight
 */
export default function useViewport (
  eventListeners = {
    scroll: undefined,
    resize: undefined
  }
) {
  update()

  const onScroll = useRef(e => {
    eventListeners.scroll && eventListeners.scroll(e, viewport)
  })
  const onResize = useRef(e => {
    eventListeners.resize && eventListeners.resize(e, viewport)
  })

  useEffect(() => {
    update()
    window.addEventListener('scroll', update)
    window.addEventListener('scroll', onScroll.current)
    window.addEventListener('resize', update)
    window.addEventListener('resize', onResize.current)

    const cleanup = () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('scroll', onScroll.current)
      window.removeEventListener('resize', update)
      window.removeEventListener('resize', onResize.current)
    }

    return cleanup
  }, [])

  const updateViewport = () => {
    update()
    typeof onScroll === 'function' && onScroll()
    typeof onResize === 'function' && onResize()
  }

  const updateEventListeners = newEventListeners => {
    if (newEventListeners.scroll) {
      window.removeEventListener('scroll', onScroll.current)
      onScroll.current = e => {
        newEventListeners.scroll(e, viewport)
      }
      window.addEventListener('scroll', onScroll.current)
    }

    if (newEventListeners.resize) {
      window.removeEventListener('resize', onResize.current)
      onResize.current = e => {
        newEventListeners.resize(e, viewport)
      }
      window.addEventListener('resize', onResize.current)
    }
  }

  return [viewport, updateViewport, updateEventListeners]
}
